import {FocusTrap} from './focus-trap';

const CONTENT_SELECTOR = 'div, p, strong, span, h1, h2, h3, h4, h5, h6, li, button, blockquote, a, label, details, summary, figcaption, code, pre, th, td, textarea, time, input[type="button"], input[type="submit"], input[type="reset"]';
const originalTextMap = new Map();

const headerTranslate = document.querySelector( '.header__translate' );
const openButtonInHeader = document.querySelector( '.translate-open' );
const openButtonInMenu = document.querySelector( '[data-toggle-translate]' );

const mainMenu = document.querySelector( '.header__main-nav' );
const responsiveToggle = document.querySelector( '.responsive-toggle' );
const mainMenuInline = document.querySelector( '.header__main-nav--inline' );
const responsiveToggleInline = document.querySelector( '.responsive-toggle--inline' );

/**
 * Detect which DeepL plugin is active and return a normalized config.
 * Supports both Acato DeepL (window.AcatoDeepL) and Yard DeepL (window.ydpl).
 */
const getDeepLConfig = () => {
  if (window.AcatoDeepL) {
    const defaultLang = window.AcatoDeepL.default_language || {};
    return {
      provider: 'acato',
      url: window.AcatoDeepL.rest_endpoint,
      headers: {
        'Content-Type': 'application/json',
        'X-DeepL-Nonce': window.AcatoDeepL.rest_nonce,
      },
      languages: window.AcatoDeepL.available_languages || [],
      defaultLanguage: defaultLang.iso_alpha2 || 'NL',
      defaultLabel: (defaultLang.native || 'Nederlands') +
        (window.AcatoDeepL.default_suffix ? ' ' + window.AcatoDeepL.default_suffix : ' (standaard)'),
      buildRequestBody: (textArray, targetLang) => ({
        text: textArray,
        target_lang: targetLang,
      }),
      parseResponse: (responseData) => responseData.data || responseData,
    };
  }

  if (window.ydpl) {
    return {
      provider: 'yard',
      url: window.ydpl.ydpl_rest_translate_url,
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': window.ydpl.ydpl_api_request_nonce,
        nonce: window.ydpl.ydpl_api_request_nonce,
      },
      languages: window.ydpl.ydpl_supported_languages || [],
      defaultLanguage: 'NL',
      defaultLabel: 'Nederlands (standaard)',
      buildRequestBody: (textArray, targetLang) => ({
        text: textArray,
        target_lang: targetLang,
        object_id: window.ydpl.ydpl_translate_post_id,
      }),
      parseResponse: (responseData) => responseData,
    };
  }

  return null;
};

const saveOriginalText = () => {
  const uniqueTextSet = new Set();

  const elements = document.querySelectorAll( CONTENT_SELECTOR );
  elements.forEach( (el) => {
    if (el instanceof HTMLElement) {
      const directText = Array.from( el.childNodes )
        .filter(
          (node) =>
            node.nodeType === Node.TEXT_NODE ||
            (node.nodeType === Node.ELEMENT_NODE && node.nodeName === "BR")
        )
        .map( (node) => node.textContent?.trim() )
        .join( " " )
        .trim();

      if (directText && directText.length > 2) {
        const normalizedText = directText.replace( /\s\s+/g, " " ); // Normalize whitespace

        if (!uniqueTextSet.has( normalizedText )) {
          // If this text hasn't been processed yet, create a new entry in the map
          originalTextMap.set( normalizedText, [el] );
          uniqueTextSet.add( normalizedText );
        } else {
          // If this text has already been processed, add the element to the list
          const elementsWithSameText = originalTextMap.get( normalizedText );
          if (elementsWithSameText) {
            elementsWithSameText.push( el );
          }
        }
      }
    }
  } );
};

const handleSelectChange = (select, config) => {
  const selectedLanguage = select.value;
  window.sessionStorage.setItem( "DeepLSelectedLanguage", selectedLanguage );

  if (selectedLanguage === config.defaultLanguage) {
    revertToOriginalText();
  } else {
    translatePage( selectedLanguage, config );
  }
};

const revertToOriginalText = () => {
  originalTextMap.forEach( (elements, originalText) => {
    elements.forEach( (el) => {
      el.textContent = originalText;
    } );
  } );
};

const translatePage = async (targetLang, config) => {
  const uniqueTextArray = Array.from( new Set( originalTextMap.keys() ) );
  saveOriginalText();
  await translateText( uniqueTextArray, targetLang, config );
};

const translateText = async (textArray, targetLang, config) => {
  try {
    const response = await fetch( config.url, {
      method: "POST",
      headers: config.headers,
      credentials: "include",
      body: JSON.stringify( config.buildRequestBody( textArray, targetLang ) ),
    } );

    if (!response.ok) {
      throw new Error(
        `Request failed with status: ${response.status}`
      );
    }

    const responseData = await response.json();
    const translations = config.parseResponse( responseData );
    document.documentElement.lang = targetLang;
    applyTranslations( translations );
  } catch (error) {
    console.error( "Error:", error );
  }
};

const applyTranslations = (translations) => {
  translations.forEach( (translation) => {
    const elements = originalTextMap.get( translation.text );
    if (elements) {
      elements.forEach( (el) => {
        el.textContent = translation.translation;
      } );
    }
  } );
};

const createSelect = (config) => {
  if (!config) {
    const error = document.createElement( "p" );
    error.style.color = "red";
    error.textContent =
      "The DeepL API is not available. Check the console for more information.";
    console.error(
      "No DeepL plugin detected. Make sure either the Acato DeepL or Yard DeepL plugin is active and configured properly."
    );
    return error;
  }

  const select = document.createElement( "select" );
  select.id = "a11y-toolbar__translate-select";
  select.classList.add( "a11y-toolbar__translate-select" );

  select.addEventListener( "change", () => handleSelectChange( select, config ) );

  const defaultOption = document.createElement( "option" );
  defaultOption.value = config.defaultLanguage;
  defaultOption.textContent = config.defaultLabel;
  select.appendChild( defaultOption );

  // Remove the default language from the list
  const filteredLanguages = config.languages.filter(
    (language) => language.iso_alpha2 !== config.defaultLanguage
  );

  // Sort the remaining supported languages by name
  const sortedLanguages = [...filteredLanguages].sort( (a, b) =>
    a.name.localeCompare( b.name )
  );

  const storedLanguage =
    window.sessionStorage.getItem( "DeepLSelectedLanguage" ) ||
    config.defaultLanguage;

  // Add options from supported languages
  sortedLanguages.forEach( (language) => {
    const option = document.createElement( "option" );
    option.value = language.iso_alpha2;
    option.textContent = language.name;
    if (option.value === storedLanguage) {
      option.selected = true;
    }
    select.appendChild( option );
  } );

  headerTranslate.querySelector( '.deepl-selector' ).appendChild( select );
};

const bindToggleTranslate = () => {
  const overlayToggles = document.querySelectorAll( '[data-toggle-translate]' );
  const overlay = document.querySelector( '.header__translate' );

  if (!overlayToggles || !overlay) {
    return;
  }

  [...overlayToggles].forEach( (toggle) => {
    toggle.addEventListener( 'click', () => {
      handleOverlayToggling( overlay );
    } );
  } );

  document.addEventListener( 'keydown', (event) => {
    if (event.key === 'Escape' && overlay.classList.contains( 'open' )) {
      handleOverlayToggling( overlay, false );
    }
  } );
};

const handleOverlayToggling = (overlay, forceRemove = false) => {
  const closeButtonInOverlay = overlay.querySelector( '.translate-close' );
  const focusTrap = new FocusTrap( overlay );

  if (mainMenu && responsiveToggle) {
    mainMenu.classList.remove( 'show' );
    responsiveToggle.setAttribute( 'aria-expanded', false )
    responsiveToggle.classList.remove( 'show' )
  }

  if (mainMenuInline && responsiveToggleInline) {
    mainMenuInline.classList.remove( 'is-open' );
    responsiveToggleInline.setAttribute( 'aria-expanded', false )
    responsiveToggleInline.classList.remove( 'is-open' )
  }

  if (!forceRemove) {
    overlay.classList.toggle( 'open' );
    openButtonInHeader.setAttribute( 'aria-expanded', overlay.classList.contains( 'open' ) );

    if (overlay.classList.contains( 'open' )) {
      setTimeout( () => {
        closeButtonInOverlay.focus();
      }, 100 );
      focusTrap.enable();
    } else {
      focusTrap.disable();

      if (window.innerWidth < 880) {
        if (openButtonInMenu) {
          openButtonInMenu.focus();
        }
      } else {
        if (openButtonInHeader) {
          openButtonInHeader.focus();
        }
      }
    }
  } else {
    overlay.classList.remove( 'open' );
    if (window.innerWidth < 880) {
      if (openButtonInMenu) {
        openButtonInMenu.focus();
      }
    } else {
      if (openButtonInHeader) {
        openButtonInHeader.focus();
      }
    }
    focusTrap.disable();
  }
};

const deepl = () => {
  const config = getDeepLConfig();
  createSelect( config );
  saveOriginalText();
  bindToggleTranslate();

  // Set the saved language and translate on page load
  if (config) {
    const savedLanguage =
      window.sessionStorage.getItem( "DeepLSelectedLanguage" ) ?? "";

    if (config.defaultLanguage !== savedLanguage && savedLanguage) {
      translatePage( savedLanguage, config );
    }
  }
}

export default deepl;